import { Cell } from '@tanstack/react-table';

import { isActionColumn } from '../../hooks/useIsActionColumn';
import { getEmbeddedTableRowRowSpan } from '../../helpers';
import { EmbeddedTableRowRowSpan, RowWithEmbeddedTableRowInfo } from '../../Types';

export type CellBasicProperties = {
  isAction: boolean;
  fixedWidth: string | undefined;
  isFirstColumn: boolean;
  isLastColumn: boolean;
};

export type RowSpanResult = {
  shouldSkipRendering: boolean;
  rowSpan: number | undefined;
};

export const calculateCellBasicProperties = <TData, TValue>(
  cell: Cell<TData, TValue>,
  index: number,
  totalCells: number,
): CellBasicProperties => ({
  isAction: isActionColumn(cell.column.columnDef),
  fixedWidth: cell.column.columnDef.meta?.style?.width,
  isFirstColumn: !index,
  isLastColumn: index === totalCells - 1,
});

/**
 * Calculates rowSpan for embedded table row cells using the LCM approach.
 *
 * Each array item spans multiple rows (rowsPerItem). Only the first row
 * for each item actually renders the cell - subsequent rows skip rendering
 * because the browser extends the cell automatically via rowSpan.
 *
 * Example: 6 total rows, 3 items → rowsPerItem=2
 * - Item 0: renders at row 0 with rowSpan=2, skips row 1
 * - Item 1: renders at row 2 with rowSpan=2, skips row 3
 * - Item 2: renders at row 4 with rowSpan=2, skips row 5
 */
const calculateEmbeddedTableRowRowSpan = <TData>(
  embeddedTableRowRowSpan: EmbeddedTableRowRowSpan,
  rowWithEmbeddedTableRowInfo: RowWithEmbeddedTableRowInfo<TData>,
): RowSpanResult => {
  if (!embeddedTableRowRowSpan.totalItems) {
    const { isFirstEmbeddedTableRow } = rowWithEmbeddedTableRowInfo;

    if (!isFirstEmbeddedTableRow) {
      return { shouldSkipRendering: true, rowSpan: undefined };
    }

    return { shouldSkipRendering: false, rowSpan: rowWithEmbeddedTableRowInfo.embeddedTableRowsCount };
  }

  // Calculate how many rows each item spans
  const rowSpan = rowWithEmbeddedTableRowInfo.embeddedTableRowsCount / embeddedTableRowRowSpan.totalItems;

  // Determine which item should be shown at this row position
  const itemIndex = Math.floor(rowWithEmbeddedTableRowInfo.embeddedTableRowIndex / rowSpan);

  // Only render on the first row for this item (other rows are spanned into)
  const isFirstRowForThisItem = rowWithEmbeddedTableRowInfo.embeddedTableRowIndex === itemIndex * rowSpan;

  if (!isFirstRowForThisItem) {
    return { shouldSkipRendering: true, rowSpan: undefined };
  }

  return { shouldSkipRendering: false, rowSpan };
};

/**
 * Determines if a cell should render and its rowSpan value.
 *
 * Basic columns (non-array): span all embedded table rows, render only on first embedded table row
 * Array columns: use LCM-based spanning with compressed row heights
 * Regular columns: no spanning
 */
export const calculateCellRowSpan = <TData>(
  cell: Cell<TData, unknown>,
  rowWithEmbeddedTableRowInfo: RowWithEmbeddedTableRowInfo<TData> | undefined,
): RowSpanResult => {
  const parentColumnPath = cell.column.parent?.id;
  const isBasicColumn = rowWithEmbeddedTableRowInfo && !parentColumnPath;

  if (isBasicColumn) {
    const shouldSkipRendering = !rowWithEmbeddedTableRowInfo.isFirstEmbeddedTableRow;
    const rowSpan = rowWithEmbeddedTableRowInfo.isFirstEmbeddedTableRow
      ? rowWithEmbeddedTableRowInfo.embeddedTableRowsCount
      : undefined;

    return { shouldSkipRendering, rowSpan };
  }

  const embeddedTableRowRowSpan = getEmbeddedTableRowRowSpan(rowWithEmbeddedTableRowInfo, parentColumnPath);

  if (embeddedTableRowRowSpan && rowWithEmbeddedTableRowInfo) {
    return calculateEmbeddedTableRowRowSpan(embeddedTableRowRowSpan, rowWithEmbeddedTableRowInfo);
  }

  return { shouldSkipRendering: false, rowSpan: undefined };
};

export const calculateCellStyle = (
  isAction: boolean,
  columnsWidth: string,
  fixedWidth: string | undefined,
): React.CSSProperties | undefined => (!isAction ? { width: columnsWidth, maxWidth: fixedWidth } : undefined);
